import { http } from '../http';
import { extrairMensagemErro } from "../../utils/apiError";

export type PapelUsuario = 'CONSULTOR' | 'TI' | 'ADMIN';

/** Recorte de situação da listagem. O default do backend é ATIVOS. */
export type SituacaoUsuarioFiltro = 'ATIVOS' | 'INATIVOS' | 'TODOS';

/**
 * Vínculos como vêm no DETALHE (`GET /user/buscar/:id`).
 *
 * ⚠️ São **dois tipos**, e não um com os dois códigos opcionais: numa regional o
 * `codigo_regional` está sempre presente, e o modal de regionais do consultor depende
 * disso. Enquanto `buscarUsuarioPorId` devolvia `any` (até a at-68) a imprecisão não
 * aparecia — o cast implícito a escondia.
 */
export interface RegionalVinculada {
  id: string;
  codigo_regional: string;
  nome: string;
  cidade?: string;
  estado?: string;
}

export interface CooperativaVinculada {
  id: string;
  codigo_cooperativa: string;
  nome: string;
  cidade?: string;
  estado?: string;
}

/** @deprecated Use `RegionalVinculada` ou `CooperativaVinculada`. */
export type VinculoResumo = RegionalVinculada | CooperativaVinculada;

/**
 * Vínculo como vem na LISTAGEM — só código e nome. A lista mostra "2 regionais · 3
 * cooperativas", e trazer o registro inteiro de cada vínculo de cada linha seria peso
 * sem uso.
 */
export interface VinculoNaLista {
  codigo_regional?: string;
  codigo_cooperativa?: string;
  nome: string;
}

/** Uma linha da listagem da central de usuários (at-68). */
export interface UsuarioListaItem {
  id: string;
  nomeCompleto: string | null;
  cpfCnpj: string | null;
  email: string;
  /** Só CONSULTOR tem (at-87). */
  volunteerCode: string | null;
  celular: string | null;
  userType: PapelUsuario;
  firstLogin: boolean;
  ativo: boolean;
  desativadoEm: string | null;
  createdAt: string;
  regionais: VinculoNaLista[];
  cooperativas: VinculoNaLista[];
}

export interface TotaisUsuarios {
  total: number;
  ativos: number;
  inativos: number;
  primeiroAcessoPendente: number;
  porPapel: Record<PapelUsuario, number>;
}

export interface UsuariosListaResponse {
  totalItems: number;
  page: number;
  limit: number;
  /** ⚠️ GLOBAIS: não seguem busca nem filtro (convenção at-10/at-28). */
  totais: TotaisUsuarios;
  items: UsuarioListaItem[];
}

export interface FiltrosListagemUsuarios {
  busca?: string;
  papel?: PapelUsuario | '';
  situacao?: SituacaoUsuarioFiltro;
  page?: number;
  limit?: number;
}

export interface UsuarioDetalhado {
  id: string;
  nomeCompleto: string | null;
  cpfCnpj: string | null;
  email: string;
  /** Só CONSULTOR tem (at-87). */
  volunteerCode: string | null;
  celular: string | null;
  userType: PapelUsuario;
  firstLogin: boolean;
  ativo: boolean;
  desativadoEm: string | null;
  createdAt: string;
  updatedAt: string;
  regionais: RegionalVinculada[];
  cooperativas: CooperativaVinculada[];
}

export interface AtualizarUsuarioPayload {
  nomeCompleto?: string;
  email?: string;
  /** `null` remove o código (TI/ADMIN, at-87). */
  volunteerCode?: string | null;
  cpfCnpj?: string;
  celular?: string;
  userType?: PapelUsuario;
  codigo_regionais?: string[];
  codigo_cooperativas?: string[];
}

export type EstadoSessao = 'ATIVA' | 'EXPIRADA' | 'REVOGADA';

export interface SessaoUsuario {
  id: string;
  userAgent: string | null;
  ip: string | null;
  criadoEm: string;
  ultimoUsoEm: string;
  expiraEm: string;
  revogadaEm: string | null;
  /** Vem PRONTO do backend (convenção at-24/at-32) — a tela não recalcula. */
  estado: EstadoSessao;
}

/**
 * Erro carregando o status HTTP — a ficha separa **404** (usuário não existe) de
 * **403** (sem permissão) e de falha de rede. Mesmo padrão do `ErroRascunhoAdesao`
 * (at-38) e do `ErroImportacaoSga` (at-32).
 */
export interface ErroUsuario extends Error {
  status: number | null;
}

const erroComStatus = (error: unknown, fallback: string): ErroUsuario => {
  const err = new Error(extrairMensagemErro(error, fallback)) as ErroUsuario;
  err.status = (error as any)?.response?.status ?? null;
  return err;
};

// 🔴 Todas as funções deste módulo LANÇAM. O critério do projeto: enriquecimento
// oportunista devolve neutro (o precedente é `sgaReconciliacaoApi`), mas leitura que o
// usuário pediu e que É o conteúdo da tela precisa falhar em voz alta — senão a central
// diria "nenhum usuário cadastrado" para uma base cheia.

export const buscarUsuarioPorId = async (userId: string): Promise<UsuarioDetalhado> => {
  try {
    const response = await http.get(`/user/buscar/${userId}`);

    return response.data;
  } catch (error: any) {
    throw erroComStatus(error, "Erro ao carregar usuário");
  }
};

export const listarUsuarios = async (
  filtros: FiltrosListagemUsuarios = {}
): Promise<UsuariosListaResponse> => {
  try {
    const response = await http.get('/user/search', {
      params: {
        // Parâmetros vazios são omitidos: mandar `papel=''` faria o backend recusar
        // com 400 em vez de entender "sem filtro".
        ...(filtros.busca ? { busca: filtros.busca } : {}),
        ...(filtros.papel ? { papel: filtros.papel } : {}),
        ...(filtros.situacao ? { situacao: filtros.situacao } : {}),
        page: filtros.page ?? 1,
        limit: filtros.limit ?? 10,
      },
    });

    return response.data;
  } catch (error: any) {
    throw erroComStatus(error, "Erro ao listar usuários");
  }
};

export const atualizarUsuario = async (
  id: string,
  payload: AtualizarUsuarioPayload
): Promise<UsuarioDetalhado> => {
  try {
    const response = await http.patch(`/user/${id}`, payload);

    return response.data;
  } catch (error: any) {
    throw erroComStatus(error, "Erro ao atualizar usuário");
  }
};

/**
 * Ativa ou desativa o acesso (at-68). Desativar encerra as sessões abertas — a
 * resposta diz quantas caíram.
 */
export const alterarSituacaoUsuario = async (
  id: string,
  ativo: boolean
): Promise<UsuarioDetalhado & { sessoesEncerradas: number }> => {
  try {
    const response = await http.patch(`/user/${id}/situacao`, { ativo });

    return response.data;
  } catch (error: any) {
    throw erroComStatus(
      error,
      ativo ? "Erro ao reativar o usuário" : "Erro ao desativar o usuário"
    );
  }
};

export const listarSessoesUsuario = async (id: string): Promise<SessaoUsuario[]> => {
  try {
    const response = await http.get(`/user/${id}/sessoes`);

    return response.data;
  } catch (error: any) {
    throw erroComStatus(error, "Erro ao carregar as sessões do usuário");
  }
};

export const encerrarSessoesUsuario = async (
  id: string
): Promise<{ sessoesEncerradas: number }> => {
  try {
    const response = await http.post(`/user/${id}/sessoes/encerrar`);

    return response.data;
  } catch (error: any) {
    throw erroComStatus(error, "Erro ao encerrar as sessões do usuário");
  }
};
